// ============================================================================
// Tax address helpers (PMS slice 3a-ii) — the client-side mirror of the API's
// TaxRegistrationValidator, so the screens say what the server will decide.
// ============================================================================
import type { TaxPack, TaxPackPlaceOfSupply } from 'api/types/tax.types';

const regionName = (code: string): string | null => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code.toUpperCase()) ?? null;
  } catch {
    return null;
  }
};

/**
 * Whether an address with this country lies in the pack's country: blank (saved
 * as the home country), the ISO code, or its English name.
 */
export const isDomesticCountry = (country: string | null | undefined, packCountryCode: string | null | undefined): boolean => {
  const c = (country ?? '').trim().toLowerCase();
  const code = (packCountryCode ?? '').trim();
  if (!c || !code) return true;
  if (c === code.toLowerCase()) return true;
  return c === (regionName(code) ?? '').toLowerCase();
};

/** The pack's states a party address can be in (geographic places only), in display order. */
export const addressStates = (pack: TaxPack | null | undefined): TaxPackPlaceOfSupply[] =>
  (pack?.placesOfSupply ?? [])
    .filter((p) => p.isGeographic)
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder || a.placeName.localeCompare(b.placeName));

/** The place named exactly (case-insensitive) — the same match the server makes. */
export const findState = (pack: TaxPack | null | undefined, name: string | null | undefined): TaxPackPlaceOfSupply | null => {
  const n = (name ?? '').trim().toLowerCase();
  if (!n) return null;
  return addressStates(pack).find((p) => p.placeName.trim().toLowerCase() === n) ?? null;
};

/** The place for a code (e.g. the state a GSTIN encodes). */
export const stateByCode = (pack: TaxPack | null | undefined, code: string | null | undefined): TaxPackPlaceOfSupply | null =>
  code ? (pack?.placesOfSupply ?? []).find((p) => p.placeCode === code) ?? null : null;

/** Whether a picker should be shown: a domestic address in a pack that lists states. */
export const usesStatePicker = (pack: TaxPack | null | undefined, country: string | null | undefined): boolean =>
  addressStates(pack).length > 0 && isDomesticCountry(country, pack?.countryCode);
